import React, { useEffect, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  PaymentAssignmentStatusId,
  PaymentType,
  TerminatedFilter,
  TestUserFilter,
  PAYMENT_TYPE_LABELS,
  PRIORITY_LABELS,
  PriorityList,
  PAYMENT_SUBMITTED_BY_BOT_FILTER_ID,
} from '../Utils/types'
import { PaymentAssignmentRole } from '../Utils/roles'
import { getMyClients } from 'enrollment-platform/api/persons'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { usePaymentCarrierListNameQuery } from '../../../api/finance-api-client'
import { usePaymentAssignmentUserOptions } from '../hooks/usePaymentAssignmentUserOptions'
import { normalizeSearch } from './helpers'
import PaymentAssignmentFilterTopBar from './PaymentAssignmentFilterTopBar'
import PaymentAssignmentFilterActiveTags from './PaymentAssignmentFilterActiveTags'
import PaymentAssignmentFilterDrawer from './PaymentAssignmentFilterDrawer'
import { ReviewBanner, ReviewBannerExit } from './styles'
import { WarningOutlined, CloseCircleOutlined } from '@ant-design/icons'
import type { PaymentAssignmentFiltersProps } from './types'

export default function PaymentAssignmentFilter({
  filters,
  updateFilter,
  clearAllFilters,
  currentPaymentAssignmentRole,
  rolePolicy,
  onSearch,
  isSearching = false,
  sort,
  migratedCount,
}: PaymentAssignmentFiltersProps) {
  const isRoleFilterLocked =
    currentPaymentAssignmentRole === PaymentAssignmentRole.MAKER
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)

  /* ------------------------------ Data fetching ----------------------------- */

  // MAKER + CHECKER users (for Maker filter & paymentSubmittedBy options)
  const {
    options: userOptions,
    isLoading: usersLoading,
    items: makerItems,
  } = usePaymentAssignmentUserOptions(PaymentAssignmentRole.MAKER)
  // ESCALATOR + CHECKER users (for Escalator filter)
  const { options: escalatorOptions, isLoading: escalatorsLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.ESCALATOR)
  const { data: carrierListData, isLoading: carriersLoading } =
    usePaymentCarrierListNameQuery()

  const checkersLoading = usersLoading

  const isOnlyChecker =
    currentPaymentAssignmentRole === PaymentAssignmentRole.CHECKER

  useEffect(() => {
    let cancelled = false

    const loadClients = async () => {
      try {
        const customers = await getMyClients()
        if (cancelled) return
        setClients(customers)
      } catch (_e) {
        if (cancelled) return
        setClients([])
      }
    }
    loadClients()

    return () => {
      cancelled = true
    }
  }, [])

  /* ----------------------------- Derived options ---------------------------- */

  const companyOptions = useMemo(
    () =>
      clients.map((client) => ({
        label: client.name,
        value: client.id,
      })),
    [clients]
  )

  const clientPublicKeysList = useMemo(
    () => companyOptions.map((o) => o.value),
    [companyOptions]
  )

  const paymentTypes: string[] = Object.values(PaymentType)

  // makerItems already contains MAKER + CHECKER users (sorted by role then name via the hook)
  const paymentSubmittedByOptions = useMemo(() => {
    const people = [...makerItems].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
    return [
      { id: PAYMENT_SUBMITTED_BY_BOT_FILTER_ID, name: 'BOT', role: undefined },
      ...people,
    ]
  }, [makerItems])

  /* ----------------------------- Search scopes ------------------------------ */

  const [carrierSearch, setCarrierSearch] = useState('')
  const [paymentTypeSearch, setPaymentTypeSearch] = useState('')
  const [prioritySearch, setPrioritySearch] = useState('')
  const [companySearch, setCompanySearch] = useState('')

  const statusOptions = useMemo(
    () => rolePolicy.allowedStatuses as PaymentAssignmentStatusId[],
    [rolePolicy.allowedStatuses]
  )

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      updateFilter('paymentPeriod', date.format('YYYY-MM'))
    } else {
      updateFilter('paymentPeriod', undefined)
    }
  }

  const currentDate = filters.paymentPeriod
    ? dayjs(filters.paymentPeriod)
    : dayjs()

  const paymentTypeScope = useMemo(() => {
    const q = normalizeSearch(paymentTypeSearch)
    if (!q) return paymentTypes
    return paymentTypes.filter((type) => {
      const label = String(PAYMENT_TYPE_LABELS[type] ?? type)
      return `${label} ${type}`.toLowerCase().includes(q)
    })
  }, [paymentTypeSearch, paymentTypes])

  const priorityScope = useMemo(() => {
    const q = normalizeSearch(prioritySearch)
    if (!q) return [...PriorityList]
    return PriorityList.filter((p) => {
      const label = String(PRIORITY_LABELS[p] ?? p)
      return `${label} ${p}`.toLowerCase().includes(q)
    })
  }, [prioritySearch])

  const companyScopeValues = useMemo(() => {
    const q = normalizeSearch(companySearch)
    if (!q) return clientPublicKeysList
    const matches = new Set(
      companyOptions
        .filter((o) => (o.label ?? '').toLowerCase().includes(q))
        .map((o) => o.value)
    )
    return clientPublicKeysList.filter((v) => matches.has(v))
  }, [clientPublicKeysList, companyOptions, companySearch])

  const carrierOptions = useMemo(
    () => (Array.isArray(carrierListData) ? [...carrierListData] : []),
    [carrierListData]
  )
  const carrierScopeNames = useMemo(() => {
    const q = normalizeSearch(carrierSearch)
    if (!q) return carrierOptions
    return carrierOptions.filter((name) =>
      (name ?? '').toLowerCase().includes(q)
    )
  }, [carrierOptions, carrierSearch])

  /* ----------------------------- Filter values ------------------------------ */

  const carriersValue = filters.carrierNames ?? []
  const paymentTypesValue = filters.paymentTypes ?? []
  const priorityLevelsValue = filters.priorityLevels ?? []
  const statusesValue = filters.statuses ?? []
  const clientPublicKeysValue = filters.clientPublicKeys ?? []
  const assigneeValue = filters.assigneeIds ?? []
  const escalatorValue = filters.escalatorIds ?? []
  const paymentSubmittedByIdsValue = filters.paymentSubmittedByIds ?? []

  const testUserFilterValue =
    filters.testUserFilter ?? TestUserFilter.NON_TEST_ONLY
  const terminatedFilterValue = filters.terminatedFilter ?? TerminatedFilter.ALL
  const relationshipsValue = filters.relationships ?? []
  const binderPaymentValue = filters.binderPayment
  const primaryPaymentMethodsValue = filters.primaryPaymentMethods ?? []

  const advancedFilterCount = useMemo(() => {
    let count = 0
    if (
      (filters.benefitEffectiveDateFrom ?? '').trim() ||
      (filters.benefitEffectiveDateTo ?? '').trim()
    )
      count++
    if (
      (filters.benefitEndDateFrom ?? '').trim() ||
      (filters.benefitEndDateTo ?? '').trim()
    )
      count++
    if (carriersValue.length > 0) count++
    if (paymentTypesValue.length > 0) count++
    if (priorityLevelsValue.length > 0) count++
    if ((filters.assigneeIds ?? []).length > 0) count++
    if ((filters.escalatorIds ?? []).length > 0) count++
    if ((filters.paymentSubmittedByIds ?? []).length > 0) count++
    if (clientPublicKeysValue.length > 0) count++
    if (testUserFilterValue !== TestUserFilter.NON_TEST_ONLY) count++
    if (terminatedFilterValue !== TerminatedFilter.ALL) count++
    if (relationshipsValue.length > 0) count++
    if (binderPaymentValue !== undefined) count++
    if (primaryPaymentMethodsValue.length > 0) count++
    if (filters.onlyMigrated === true) count++
    return count
  }, [
    filters.benefitEffectiveDateFrom,
    filters.benefitEffectiveDateTo,
    filters.benefitEndDateFrom,
    filters.benefitEndDateTo,
    carriersValue.length,
    paymentTypesValue.length,
    priorityLevelsValue.length,
    filters.assigneeIds,
    filters.escalatorIds,
    filters.paymentSubmittedByIds,
    clientPublicKeysValue.length,
    testUserFilterValue,
    terminatedFilterValue,
    relationshipsValue.length,
    binderPaymentValue,
    primaryPaymentMethodsValue.length,
    filters.onlyMigrated,
  ])

  const handleDrawerClose = () => {
    onSearch()
    setDrawerOpen(false)
  }

  const isOnlyMigratedActive = filters.onlyMigrated === true

  // Toggle the "needs review" (migrated) filter, then run the search on the
  // next tick so onSearch reads the freshly-staged filter value.
  const [pendingMigratedSearch, setPendingMigratedSearch] = useState(false)
  useEffect(() => {
    if (pendingMigratedSearch) {
      onSearch()
      setPendingMigratedSearch(false)
    }
  }, [pendingMigratedSearch, onSearch])

  const handleToggleOnlyMigrated = () => {
    updateFilter('onlyMigrated', isOnlyMigratedActive ? undefined : true)
    setPendingMigratedSearch(true)
  }

  const showReviewBanner = (migratedCount ?? 0) > 0 || isOnlyMigratedActive

  /* -------------------------------- Render --------------------------------- */

  return (
    <>
      <PaymentAssignmentFilterTopBar
        currentDate={currentDate}
        handleDateChange={handleDateChange}
        statusOptions={statusOptions}
        statusesValue={statusesValue}
        updateFilter={updateFilter}
        onSearch={onSearch}
        isSearching={isSearching}
        advancedFilterCount={advancedFilterCount}
        setDrawerOpen={setDrawerOpen}
        isOnlyChecker={isOnlyChecker}
        filters={filters}
        sort={sort}
      />

      {showReviewBanner && (
        <ReviewBanner
          type="button"
          $active={isOnlyMigratedActive}
          onClick={handleToggleOnlyMigrated}
          disabled={isSearching}
          aria-pressed={isOnlyMigratedActive}
        >
          <WarningOutlined />
          <span style={{ flex: 1 }}>
            {`${migratedCount ?? 0} benefits need review: Benefit ID changed.`}
          </span>
          {isOnlyMigratedActive ? (
            <ReviewBannerExit>
              Exit <CloseCircleOutlined />
            </ReviewBannerExit>
          ) : null}
        </ReviewBanner>
      )}

      <PaymentAssignmentFilterActiveTags
        statusesValue={statusesValue}
        paymentTypesValue={paymentTypesValue}
        priorityLevelsValue={priorityLevelsValue}
        updateFilter={updateFilter}
        onSearch={onSearch}
      />

      <PaymentAssignmentFilterDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onSearch={onSearch}
        clearAllFilters={clearAllFilters}
        filters={filters}
        updateFilter={updateFilter}
        rolePolicy={rolePolicy}
        isRoleFilterLocked={isRoleFilterLocked}
        statusOptions={statusOptions}
        statusesValue={statusesValue}
        paymentTypes={paymentTypes}
        paymentTypesValue={paymentTypesValue}
        paymentTypeScope={paymentTypeScope}
        setPaymentTypeSearch={setPaymentTypeSearch}
        priorityLevelsValue={priorityLevelsValue}
        priorityScope={priorityScope}
        setPrioritySearch={setPrioritySearch}
        paymentSubmittedByIdsValue={paymentSubmittedByIdsValue}
        paymentSubmittedByOptions={paymentSubmittedByOptions}
        usersLoading={usersLoading}
        checkersLoading={checkersLoading}
        terminatedFilterValue={terminatedFilterValue}
        testUserFilterValue={testUserFilterValue}
        clientPublicKeysValue={clientPublicKeysValue}
        companyOptions={companyOptions}
        companyScopeValues={companyScopeValues}
        setCompanySearch={setCompanySearch}
        carriersValue={carriersValue}
        carrierOptions={carrierOptions}
        carrierScopeNames={carrierScopeNames}
        carriersLoading={carriersLoading}
        setCarrierSearch={setCarrierSearch}
        assigneeValue={assigneeValue}
        userOptions={userOptions}
        escalatorValue={escalatorValue}
        escalatorOptions={escalatorOptions}
        escalatorsLoading={escalatorsLoading}
        relationshipsValue={relationshipsValue}
        binderPaymentValue={binderPaymentValue}
        primaryPaymentMethodsValue={primaryPaymentMethodsValue}
      />
    </>
  )
}
